import {
  ConfigFile,
  QuickshipConfigFile
} from "@/models/configurator/config_file";
import axios, { AxiosResponse } from "axios";
import {
  APIError,
  AuthError,
  BadRequest,
  NotFoundError,
  PermissionsError
} from "./error_service";
import { AppHeaders } from "@/models/api/api";
import { ResolvedSharedConfig } from "@/models/configurator/shared_config";

axios.defaults.headers = { apikey: process.env.VUE_APP_API_KEY } as AppHeaders;
axios.defaults.baseURL = process.env.VUE_APP_API_NODE_URL;

/**
 * Creates a new Config File Service

 * @example
 * const configFileService = new ConfigFileService();
 * configFileService.getFile();
 */

export default class ConfigFileService {
  /**
   * @param {string} id string value of ObjectId
   * @returns {ConfigFile} - config file
   */
  public async getFile(id: string): Promise<ConfigFile> {
    let file;
    const url = "/configurator/" + id;
    try {
      const res: AxiosResponse = await axios.get(url);
      file = res.data.data;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        throw new NotFoundError(
          "Configuration file not found.",
          err.response.status
        );
      } else if (err.response && err.response.status === 403) {
        throw new PermissionsError(
          err.response.data.message,
          err.response.status
        );
      } else if (err.response && err.response.status === 401) {
        throw new AuthError(err.response.data.message, err.response.status);
      } else {
        throw new APIError(
          "Something went wrong, please try again.",
          err.response.status
        );
      }
    }
    return file;
  }

  /**
   *
   * @returns {[ConfigFile]} - config files
   */
  public async getQuickshipFiles(): Promise<QuickshipConfigFile[]> {
    let files;
    const url = "/quickship";
    try {
      const res: AxiosResponse = await axios.get(url);
      files = res.data.data;
    } catch (err) {
      if (err.response && err.response.status === 400) {
        throw new BadRequest(err.response.data.message, err.response.status);
      } else if (err.response && err.response.status === 403) {
        throw new PermissionsError(
          err.response.data.message,
          err.response.status
        );
      } else if (err.response && err.response.status === 401) {
        throw new AuthError(err.response.data.message, err.response.status);
      } else if (err.response && err.response.status === 404) {
        throw new NotFoundError(
          "Configuration files not found.",
          err.response.status
        );
      } else {
        throw new APIError(
          "Something went wrong, please try again.",
          err.response.status
        );
      }
    }
    return files;
  }

  /**
   * FUPOF-193: resolve a Shade Designer configuration short code into
   * Shade-Quote-shaped lines, ready to preload into the configurator.
   *
   * Distinct from getShareFile above, which returns the raw config document for
   * Shade Designer's viewer. This route does the SD -> SQ transform server-side
   * (finial finish normalization, the spigot stem sentinel, promoting a top
   * cover to its own accessory line) so the whole design arrives in one
   * round-trip already in the shape the order form's pages accept.
   *
   * The API's 404 message is passed through verbatim rather than replaced: the
   * difference between "Share link has expired." and "Configuration file does
   * not exist" is the whole answer to the rep's question, and a generic
   * "not found" would throw it away.
   */
  public async resolveShareCode(
    shareId: string
  ): Promise<ResolvedSharedConfig> {
    const url = `/configurator/share/${encodeURIComponent(
      shareId
    )}/resolve?client=shade-quote`;
    try {
      const res: AxiosResponse = await axios.get(url);
      return res.data.data as ResolvedSharedConfig;
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.message;
      if (status === 404) {
        throw new NotFoundError(
          message || "Configuration file does not exist",
          status
        );
      } else if (status === 400) {
        throw new BadRequest(
          message || "That configuration code cannot be added to a quote.",
          status
        );
      }
      throw new APIError("Something went wrong, please try again.", status);
    }
  }

  public async getShareFile(shareId: string): Promise<ConfigFile> {
    let file;
    const url = "/configurator/share/" + shareId;
    try {
      const res: AxiosResponse = await axios.get(url);
      file = res.data.data;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        throw new NotFoundError(
          "Configuration file not found.",
          err.response.status
        );
      } else {
        throw new APIError(
          "Something went wrong, please try again.",
          err.response.status
        );
      }
    }
    return file;
  }
}
